import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import skillIcons from '../../Utils/skillIcons';
import './Work.css';

gsap.registerPlugin(ScrollTrigger);

/**
 * Work, as two panes.
 *
 * Left pane is the stack, as a grid of brand marks. Right pane is the
 * projects, as a horizontal rail you push sideways.
 *
 * The rail is a plain overflow-x scroller with scroll snapping, not a
 * scroll-hijack: the page keeps scrolling vertically the whole time, the
 * rail only moves when you drag, shift-wheel or tab through it. That is
 * what keeps it from repeating the pinned-pan problem.
 */
function Work({ data, standalone = false }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
        const tw = gsap.from('.pane', {
          scrollTrigger: { trigger: '.panes', start: 'top 85%' },
          opacity: 0,
          y: 28,
          duration: 0.75,
          stagger: 0.1,
          ease: 'power3.out',
        });
        return () => tw.kill();
      });
    }, rootRef);

    return () => ctx.revert();
  }, [data]);

  return (
    <>
      {standalone && (
        <Helmet>
          <title>Work, Damilare Oyedeji</title>
          <meta
            name="description"
            content="Selected engineering work across fintech, edtech, SaaS, mobile and developer tooling."
          />
        </Helmet>
      )}

      <section className="work act act--ruled" id="work" ref={rootRef}>
        <div className="shell">
          <header className="work__head">
            <span className="eyebrow">My work</span>
            <h2 className="work__heading">
              Things I have <span className="accent">shipped</span>
            </h2>
          </header>

          <div className="panes">

            {/* ---- Skills ---- */}
            <article className="pane pane--skills">
              <div className="pane__body">
                <ul className="skills">
                  {skillIcons.map((icon) => (
                    <li className="skills__item" key={icon.label}>
                      <svg
                        viewBox="0 0 24 24"
                        role="img"
                        aria-label={icon.label}
                        style={{
                          '--brand-light': icon.hexLight,
                          '--brand-dark': icon.hexDark,
                        }}
                      >
                        <path d={icon.path} />
                      </svg>
                    </li>
                  ))}
                </ul>
              </div>

              <footer className="pane__foot">
                <span className="pane__label">Most use</span>
                <h3 className="pane__title">Skills</h3>
              </footer>
            </article>

            {/* ---- Projects ---- */}
            <article className="pane pane--projects">
              <div className="pane__body">
                <ul className="rail" tabIndex={0} aria-label="Selected projects, scroll sideways">
                  {data.map((p) => {
                    const live = Boolean(p.link);
                    const Tag = live ? 'a' : 'div';

                    return (
                      <li className="rail__item" key={p.id}>
                        <Tag
                          className={`shot${live ? ' shot--live' : ''}`}
                          {...(live
                            ? {
                                href: p.link,
                                target: '_blank',
                                rel: 'noreferrer',
                                'aria-label': `${p.title}, opens in a new tab`,
                              }
                            : {})}
                        >
                          <span className="shot__frame">
                            {p.cover ? (
                              <img src={p.cover} alt={`${p.title} interface`} loading="lazy" decoding="async" />
                            ) : (
                              /* No public screenshot exists for this one. A
                                 wordmark plate is honest; a mocked-up product
                                 shot would not be. */
                              <span className="shot__plate">{p.title}</span>
                            )}
                          </span>
                          <span className="shot__row">
                            <span className="shot__text">
                              <span className="shot__name">{p.title}</span>
                              <span className="shot__cat">{p.category}</span>
                            </span>

                            {/* Filled badge rather than a bare glyph, so the
                                affordance is legible at a glance. */}
                            {live && (
                              <span className="shot__go">
                                <ArrowUpRight size={17} strokeWidth={2.2} aria-hidden="true" />
                              </span>
                            )}
                          </span>
                        </Tag>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* No pane-level action. Each project in the rail carries its
                  own link, so a second one here would just be ambiguous. */}
              <footer className="pane__foot">
                <span className="pane__label">Showcase</span>
                <h3 className="pane__title">Projects</h3>
              </footer>
            </article>

          </div>
        </div>
      </section>
    </>
  );
}

export default Work;
